import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LifeBuoy, Search, Filter, Loader2, Clock, Plus, AlertCircle } from 'lucide-react';
import { getUserFacingBookingId } from '../../utils/userFacingBookingId';
import {
  AGENT_STATUS_TABS,
  AGENT_LIST_POLL_MS,
  getCategoryLabel,
  PRIORITY_COLORS,
  SUPPORT_API,
  SUPPORT_CATEGORIES,
} from '../../components/support/supportConstants';
import { SupportStatusBadge } from '../../components/support/SupportStatusBadge';
import { mergeTicketRows } from '../../utils/agentSupportList';

const BASE_PATH = '/agent/support-tickets';
const LIMIT = 25;

const AgentSupportTickets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [tab, setTab] = useState(initialStatus);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setListError('');
    try {
      const r = await axios.get(`${SUPPORT_API}/support/tickets`, {
        withCredentials: true,
        params: {
          page,
          limit: LIMIT,
          status: tab === 'all' ? 'all' : tab,
          category,
          search: searchDebounced.trim() || undefined,
        },
      });
      const tickets = r.data.tickets || [];
      setRows((prev) => (silent ? mergeTicketRows(prev, tickets) : tickets));
      setTotal(r.data.total || 0);
      setCounts(r.data.counts || {});
    } catch (e) {
      if (!silent) {
        setRows([]);
        setTotal(0);
        setCounts({});
      }
      setListError(e.response?.data?.detail || 'Could not load tickets');
    }
    if (!silent) setLoading(false);
  }, [page, tab, category, searchDebounced]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, AGENT_LIST_POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status && status !== tab) setTab(status);
  }, [searchParams]);

  const setTabAndUrl = (id) => {
    setTab(id);
    setPage(1);
    if (id === 'all') {
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ status: id }, { replace: true });
    }
  };

  const actionRows = useMemo(
    () => rows.filter((t) => t.status === 'need_reply'),
    [rows],
  );

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const renderRow = (t, highlight = false) => (
    <tr
      key={t.id}
      onClick={() => navigate(`${BASE_PATH}/${t.id}`)}
      data-testid={`agent-support-row-${t.id}`}
      className={`border-t border-gray-100 hover:bg-teal-50/50 cursor-pointer ${
        highlight ? 'bg-amber-50/80' : ''
      }`}
    >
      <td className="px-4 py-2 font-mono text-xs text-teal-700">{t.reference}</td>
      <td className="px-4 py-2 font-mono text-xs text-gray-600">
        {getUserFacingBookingId({ booking_id: t.booking_id })}
      </td>
      <td className="px-4 py-2">{getCategoryLabel(t.category)}</td>
      <td className="px-4 py-2 max-w-[260px] truncate">{t.subject || '—'}</td>
      <td className={`px-4 py-2 capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</td>
      <td className="px-4 py-2 text-xs text-gray-500">
        {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-2">
        <SupportStatusBadge status={t.status} variant="agent" />
      </td>
    </tr>
  );

  return (
    <div className="max-w-[1200px] mx-auto" data-testid="agent-support-list">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1B375B] flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-teal-600" /> My Support
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Track requests and chat with the ACF support team.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Total: <strong className="text-gray-900">{counts.all ?? 0}</strong>
          </span>
          <button
            type="button"
            onClick={() => navigate('/agent/bookings')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold shadow-sm hover:opacity-95"
            data-testid="agent-support-raise-cta"
          >
            <Plus className="w-4 h-4" /> Raise ticket
          </button>
        </div>
      </div>

      {!loading && actionRows.length > 0 && tab !== 'need_reply' && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="agent-support-action-required">
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm mb-2">
            <AlertCircle className="w-4 h-4" /> Action required
          </div>
          <div className="space-y-2">
            {actionRows.slice(0, 5).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => navigate(`${BASE_PATH}/${t.id}`)}
                className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white border border-amber-100 hover:border-amber-300 text-sm"
                data-testid={`agent-action-ticket-${t.id}`}
              >
                <span className="font-mono text-teal-700 text-xs">{t.reference}</span>
                <span className="truncate flex-1 mx-2 text-gray-800">{t.subject || getCategoryLabel(t.category)}</span>
                <SupportStatusBadge status={t.status} variant="agent" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit overflow-x-auto">
        {AGENT_STATUS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`agent-support-tab-${t.id}`}
            onClick={() => setTabAndUrl(t.id)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition whitespace-nowrap ${
              tab === t.id
                ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === t.id ? 'bg-white/20 text-white' : t.tone}`}>
              {counts[t.id] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          data-testid="agent-support-category"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
        >
          <option value="all">All Categories</option>
          {Object.entries(SUPPORT_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="agent-support-search"
            type="text"
            placeholder="Search by ref, booking, subject"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-full"
          />
        </div>
      </div>

      {listError && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{listError}</div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500 space-y-3">
            <p>
              {(counts.all ?? 0) === 0
                ? "You haven't raised any support tickets yet."
                : 'No tickets match your filters.'}
            </p>
            {(counts.all ?? 0) === 0 && (
              <button
                type="button"
                onClick={() => navigate('/agent/bookings')}
                className="text-teal-700 font-semibold hover:underline"
              >
                Go to Flight Bookings to raise a ticket
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium text-gray-600">Reference</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Booking</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Category</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Subject</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Priority</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Last Update</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => renderRow(t, t.status === 'need_reply'))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-gray-100">
              {rows.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`${BASE_PATH}/${t.id}`)}
                  data-testid={`agent-support-card-${t.id}`}
                  className={`block w-full text-left p-4 hover:bg-teal-50/50 ${
                    t.status === 'need_reply' ? 'bg-amber-50/60' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-teal-700">{t.reference}</div>
                      <div className="font-semibold text-gray-900 mt-0.5 text-sm">
                        {t.subject || getCategoryLabel(t.category)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {getCategoryLabel(t.category)} · {getUserFacingBookingId({ booking_id: t.booking_id })}
                      </div>
                    </div>
                    <SupportStatusBadge status={t.status} variant="agent" />
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : '—'}
                  </div>
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
                <span className="text-gray-500">Page {page} / {totalPages} · {total} tickets</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                          className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                  <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                          className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentSupportTickets;
